import React, { useEffect, Suspense } from 'react';
import { isPortalDark, onPortalThemeChange } from './utils/portalTheme';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { CircularProgress, Box } from '@mui/material';

// Import store
import { store } from './store/store';

// Import pages - Dashboard and Login are critical, load immediately
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';

// Import components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Import services (auth check handled by ProtectedRoute)

// Lazy load less-frequently used pages (reduces initial bundle)
const SpreadDetails = React.lazy(() => import('./pages/SpreadDetails'));
const Settings = React.lazy(() => import('./pages/Settings'));

// Loading fallback component for lazy routes
const LazyLoadFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress size={40} />
  </Box>
);

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    success: {
      main: '#4caf50',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// Pre-computed toast options to avoid re-creating on every render
const DARK_TOAST_OPTIONS = {
  duration: 4000,
  style: { background: '#333', color: '#fff' },
};
const LIGHT_TOAST_OPTIONS = {
  duration: 4000,
  style: { background: '#fff', color: '#333' },
};

// Dark theme (matching imbalances dashboard)
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#0a0a0a', // Darker background
      paper: '#1a1a1a',   // Darker paper
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    grey: {
      100: '#2a2a2a',
      200: '#3a3a3a',
      300: '#4a4a4a',
      400: '#5a5a5a',
      500: '#6a6a6a',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#3a3a3a',
        },
      },
    },
  },
});

function App() {
  // Theme is chosen once in the portal and published as the portal_theme
  // cookie; this dashboard follows it rather than keeping its own preference.
  const [isDarkMode, setIsDarkMode] = React.useState(() => isPortalDark());

  // The portal is usually a different tab, so re-read the cookie when this one
  // regains focus.
  React.useEffect(
    () => onPortalThemeChange((next) => setIsDarkMode(next === 'dark')),
    []
  );

  return (
    <Provider store={store}>
      <ThemeProvider theme={isDarkMode ? darkTheme : theme}>
        <CssBaseline />
        <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout isDarkMode={isDarkMode}>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/spread/:ticker1/:ticker2"
                element={
                  <ProtectedRoute>
                    <Layout isDarkMode={isDarkMode}>
                      <Suspense fallback={<LazyLoadFallback />}>
                        <SpreadDetails />
                      </Suspense>
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout isDarkMode={isDarkMode}>
                      <Suspense fallback={<LazyLoadFallback />}>
                        <Settings />
                      </Suspense>
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
          <Toaster
            position="top-right"
            toastOptions={isDarkMode ? DARK_TOAST_OPTIONS : LIGHT_TOAST_OPTIONS}
          />
        </ThemeProvider>
    </Provider>
  );
}

export default App;